import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectCurrentUser,
  selectIsAuthenticated,
} from "@/redux/authentication/selectors";

import BaseSpinner from "../Spiner/BaseSpinner";
import BaseButton from "../Button/BaseButton";
import { Roles } from "@/shared/utils/constants";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";

import "./styles.css";

const AccessDenied = ({ message }: { message?: string }) => {
  const { t } = useTranslation();

  return (
    <div className="access-denied-container">
      <h1 className="header-l-medium">{t("labels.accessDenied")}</h1>
      <p className="body-m-regular">
        {message || "You don't have permission to access this page."}
      </p>
      <BaseButton size="small" to={"/"} type={BUTTON_TYPES.LINK}>
        {t("labels.returnToHome")}
      </BaseButton>
    </div>
  );
};

const ProtectedRoute = ({
  element,
  roles = [],
  organizationRoles = [],
}: {
  element: React.ReactElement;
  roles: string[];
  organizationRoles: string[];
}) => {
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      checkAccess();
      setIsLoading(false);
    }
  }, [currentUser, roles, organizationRoles]);

  const checkAccess = () => {
    if (!currentUser) return;

    if (currentUser.role === Roles.ADMIN.value) {
      if (!roles || !roles.includes(Roles.ADMIN.value)) {
        setHasAccess(false);
        return;
      }

      setHasAccess(true);
      return;
    }

    if (currentUser.role === Roles.USER.value) {
      const hasAllowedUserRole = roles && roles.includes(Roles.USER.value);

      if (!hasAllowedUserRole) {
        setHasAccess(false);
        return;
      }

      setHasAccess(
        currentUser.organizationRole != null &&
          organizationRoles.includes(currentUser.organizationRole),
      );
      return;
    }

    setHasAccess(false);
  };

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect_to=${location.pathname}`} />;
  }

  if (isLoading) {
    return <BaseSpinner />;
  }

  if (!currentUser) {
    return <AccessDenied message="Error loading user information" />;
  }

  return hasAccess ? element : <AccessDenied />;
};

export default ProtectedRoute;
